'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type SessionUser = {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  propertyIds?: string[];
  chatVisitorId?: string;
};

export type SessionPayload = {
  user?: SessionUser;
  expires?: string;
};

export type SessionState = {
  data: SessionPayload | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  refresh: () => Promise<void>;
};

export const AuthSessionContext = createContext<SessionState | null>(null);

export function useAuthSession(): SessionState {
  const context = useContext(AuthSessionContext);
  if (context) return context;
  const [data, setData] = useState<SessionPayload | null>(null);
  const [status, setStatus] = useState<SessionState['status']>('loading');

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/auth/session', {
        cache: 'no-store',
        credentials: 'include',
      });
      const payload = await res.json();
      const session = payload?.session ?? null;
      setData(session);
      setStatus(session?.user ? 'authenticated' : 'unauthenticated');
    } catch {
      setData(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, status, refresh };
}
